import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { Badge, Button, Card, Col, DatePicker, Empty, Row, Select, Space, Table, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { api, hasRole, money, monthRangeOf, showError, useSub } from './shared';
import WorksTable from './WorksTable';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const ProblemsTab = ({ preset }) => {
    const { me, meta, subcontractors } = useSub();
    const canEdit = hasRole(me, 'sdo');
    const [period, setPeriod] = useState(() => monthRangeOf(dayjs().subtract(1, 'month')));
    const [reason, setReason] = useState(null);
    const [data, setData] = useState({ items: [], summary: {} });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!preset) return;
        if (preset.period) setPeriod(monthRangeOf(preset.period));
        setReason(preset.reason || null);
    }, [preset]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get('/works', {
                params: { periodFrom: period[0].format('YYYY-MM'), periodTo: period[1].format('YYYY-MM') }
            });
            setData(d);
        } catch (error) {
            showError(error);
        } finally {
            setLoading(false);
        }
    }, [period]);

    useEffect(() => {
        load();
    }, [load]);

    const open = useMemo(() => (data.items || []).filter(w => w.state !== 'closed'), [data]);
    const reasons = useMemo(() => {
        const list = [];
        Object.entries(data.summary?.by_error || {}).forEach(([k, n]) => list.push({ key: k, title: meta.errors?.[k] || k, count: n, kind: 'error' }));
        Object.entries(data.summary?.by_warning || {}).forEach(([k, n]) => list.push({ key: k, title: meta.warnings?.[k] || k, count: n, kind: 'warning' }));
        return list.sort((a, b) => b.count - a.count);
    }, [data, meta]);

    const items = useMemo(
        () => (reason ? open.filter(w => w.errors.includes(reason) || w.warnings.includes(reason)) : open.filter(w => w.state === 'error' || w.warnings.length)),
        [open, reason]
    );

    const unboundExecutors = useMemo(() => {
        if (reason !== 'no_subcontractor') return [];
        const map = new Map();
        items.forEach(w => {
            const k = w.executor;
            if (!map.has(k)) map.set(k, { executor: k, executor_id: w.executor_id, region: w.region, works: 0, amount: 0 });
            const e = map.get(k);
            e.works += 1;
            e.amount += w.amount;
        });
        return [...map.values()].sort((a, b) => b.works - a.works);
    }, [items, reason]);

    const bind = async (row, subId) => {
        try {
            if (row.executor_id) await api.put(`/executors/${row.executor_id}`, { subcontractor_id: subId });
            else await api.post('/executors', { fio: row.executor, region: row.region, subcontractor_id: subId });
            message.success(`${row.executor} привязан`);
            load();
        } catch (error) {
            showError(error);
        }
    };

    return (
        <>
            <Space style={{ marginBottom: 12 }} wrap>
                <Text>Период:</Text>
                <RangePicker
                    picker="month"
                    value={period}
                    onChange={v => v?.[0] && v?.[1] && setPeriod(monthRangeOf(v))}
                    format="MMMM YYYY"
                    allowClear={false}
                    placeholder={['С месяца', 'По месяц']}
                />
                <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Обновить</Button>
                <Text type="secondary">Работ с проблемами: {(data.items || []).filter(w => w.state === 'error').length} из {data.summary?.total || 0}</Text>
            </Space>

            <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
                <Col>
                    <Card size="small" hoverable onClick={() => setReason(null)} style={{ borderColor: !reason ? '#1677ff' : undefined, minWidth: 170 }}>
                        <Text strong>Все проблемы</Text>
                        <div style={{ fontSize: 20 }}>{(data.items || []).filter(w => w.state === 'error').length}</div>
                    </Card>
                </Col>
                {reasons.map(r => (
                    <Col key={r.key}>
                        <Card size="small" hoverable onClick={() => setReason(r.key)} style={{ borderColor: reason === r.key ? '#1677ff' : undefined, minWidth: 170 }}>
                            <Badge status={r.kind === 'error' ? 'error' : 'warning'} text={<Text strong>{r.title}</Text>} />
                            <div style={{ fontSize: 20 }}>{r.count}</div>
                        </Card>
                    </Col>
                ))}
            </Row>

            {reason === 'no_subcontractor' && (
                <Card size="small" title="Исполнители без субподрядчика — привяжите один раз, система запомнит" style={{ marginBottom: 12 }}>
                    {unboundExecutors.length ? (
                        <Table
                            size="small"
                            rowKey="executor"
                            dataSource={unboundExecutors}
                            pagination={{ pageSize: 20 }}
                            columns={[
                                { title: 'Исполнитель', dataIndex: 'executor', render: v => v || <Text type="danger">не указан</Text> },
                                { title: 'Регион', dataIndex: 'region' },
                                { title: 'Работ', dataIndex: 'works', align: 'right' },
                                { title: 'Сумма', dataIndex: 'amount', align: 'right', render: money },
                                {
                                    title: 'Субподрядчик',
                                    width: 280,
                                    render: (_, r) => (canEdit && r.executor ? (
                                        <Select
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="Выберите субподрядчика"
                                            style={{ width: 260 }}
                                            onChange={v => bind(r, v)}
                                            options={subcontractors.filter(x => x.active).map(x => ({ value: x.id, label: x.short_name || x.full_name }))}
                                        />
                                    ) : '—')
                                }
                            ]}
                        />
                    ) : <Empty description="Нет" />}
                </Card>
            )}

            <WorksTable items={items} loading={loading} />
        </>
    );
};

export default ProblemsTab;
